<!-- src/views/odontogram/OdontogramForm.vue -->
<template>
  <div class="odontogram-form">
    <!-- Back button and page title -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <router-link :to="returnPath" class="btn btn-outline-primary">
        <i class="bi bi-arrow-left me-2"></i>Volver
      </router-link>
      <h1>{{ isEditMode ? 'Editar Odontograma' : 'Nuevo Odontograma' }}</h1>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ error }}
    </div>

    <!-- Form content -->
    <div v-else class="card">
      <div class="card-header">
        <h5 class="mb-0">
          Odontograma de {{ patientName }}
        </h5>
      </div>
      <div class="card-body">
        <!-- Main odontogram component -->
        <Odontogram 
          :odontogramId="odontogramId" 
          :isEditable="true" 
          @updated="onOdontogramUpdated"
        />

        <!-- Additional form fields can be added here -->
        <div class="mt-4">
          <label for="notes" class="form-label">Observaciones generales</label>
          <textarea
            id="notes"
            v-model="form.notes"
            class="form-control"
            rows="3"
            placeholder="Agregar observaciones generales sobre el odontograma..."
          ></textarea>
        </div>
      </div>
      <div class="card-footer d-flex justify-content-between">
        <span class="text-muted">Haga clic en un diente para ver o editar su estado</span>
        <div>
          <button
            type="button"
            class="btn btn-outline-secondary me-2" 
            @click="cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="saveOdontogram"
            :disabled="saving"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Guardar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Odontogram from '@/components/odontogram/Odontogram.vue';
import { odontogramService, patientService, clinicalHistoryService } from '@/services/api';

export default {
  name: 'OdontogramForm',
  components: {
    Odontogram
  },
  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      odontogram: null,
      patient: null,
      clinicalHistory: null,
      form: {
        notes: ''
      }
    };
  },
  computed: {
    odontogramId() {
      return this.$route.params.id;
    },
    isEditMode() {
      return this.$route.path.includes('/edit');
    },
    clinicalHistoryId() {
      return this.$route.query.clinical_history;
    },
    patientName() {
      if (this.patient) {
        return `${this.patient.first_name} ${this.patient.last_name}`;
      }
      return 'Paciente';
    },
    returnPath() {
      if (this.isEditMode && this.odontogram) {
        return `/odontograms/${this.odontogram.id}`;
      } else if (this.patient) {
        return `/patients/${this.patient.id}`;
      }
      return '/';
    }
  },
  async created() {
    try {
      if (this.isEditMode) {
        await this.fetchExistingOdontogram();
      } else {
        await this.setupNewOdontogram();
      }
    } catch (error) {
      console.error('Error loading data:', error);
      this.error = 'No se pudieron cargar los datos necesarios. Por favor, intenta de nuevo.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchExistingOdontogram() {
      // Fetch odontogram data
      const odontogramResponse = await odontogramService.get(this.odontogramId);
      this.odontogram = odontogramResponse.data;
      
      // Get clinical history and then patient
      const clinicalHistoryId = this.odontogram.clinical_history;
      const clinicalHistoryResponse = await clinicalHistoryService.get(clinicalHistoryId);
      this.clinicalHistory = clinicalHistoryResponse.data;
      
      const patientId = this.clinicalHistory.patient;
      const patientResponse = await patientService.get(patientId);
      this.patient = patientResponse.data;
      
      // Set form data
      this.form.notes = this.odontogram.notes || '';
    },
    async setupNewOdontogram() {
      if (!this.clinicalHistoryId) {
        this.error = 'Para crear un odontograma, se necesita un historial clínico.';
        return;
      }
      
      // Get clinical history and then patient
      const clinicalHistoryResponse = await clinicalHistoryService.get(this.clinicalHistoryId);
      this.clinicalHistory = clinicalHistoryResponse.data;
      
      const patientId = this.clinicalHistory.patient;
      const patientResponse = await patientService.get(patientId);
      this.patient = patientResponse.data;
      
      // Create a new odontogram object
      this.odontogram = {
        clinical_history: parseInt(this.clinicalHistoryId),
        data: {},
        notes: ''
      };
      
      // Save the new odontogram to get an ID
      const response = await odontogramService.create(this.odontogram);
      this.odontogram = response.data;
    },
    onOdontogramUpdated() {
      // This can be used to track changes if needed
    },
    cancel() {
      // Navigate back to the previous page
      this.$router.push(this.returnPath);
    },
    async saveOdontogram() {
      this.saving = true;
      try {
        // Update the odontogram notes
        const odontogramData = {
          ...this.odontogram,
          notes: this.form.notes
        };
        
        await odontogramService.update(this.odontogram.id, odontogramData);
        
        // Navigate back to the appropriate page
        if (this.isEditMode) {
          this.$router.push(`/odontograms/${this.odontogram.id}`);
        } else {
          this.$router.push(`/patients/${this.patient.id}`);
        }
      } catch (error) {
        console.error('Error saving odontogram:', error);
        this.error = 'No se pudo guardar el odontograma. Por favor, intenta de nuevo.';
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>